'use client';

import { Box, Button, Dialog, HStack, Portal, Switch, Text, VStack } from '@chakra-ui/react';
import { Copy, Link2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { SessionVisibility } from '@/types/research-session';
import { createSessionLink } from '@/utils/research-links';

interface SessionShareDialogProps {
  open: boolean;
  sessionId: string;
  visibility: SessionVisibility;
  onOpenChange: (open: boolean) => void;
  onVisibilityChange: (visibility: SessionVisibility) => Promise<void>;
}

export function SessionShareDialog({
  open,
  sessionId,
  visibility,
  onOpenChange,
  onVisibilityChange,
}: SessionShareDialogProps) {
  const [saving, setSaving] = useState(false);

  const handleToggle = async (checked: boolean) => {
    const nextVisibility: SessionVisibility = checked ? 'workspace' : 'private';
    setSaving(true);
    try {
      await onVisibilityChange(nextVisibility);
      toast.success(checked ? 'Workspace viewing enabled' : 'Workspace viewing disabled');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update sharing');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    const path = createSessionLink(sessionId);
    const link =
      typeof window === 'undefined' ? path : new URL(path, window.location.origin).toString();
    await navigator.clipboard.writeText(link);
    toast.success('Thread link copied');
  };

  return (
    <Dialog.Root open={open} onOpenChange={event => onOpenChange(event.open)} placement="center">
      <Portal>
        <Dialog.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(4px)" />
        <Dialog.Positioner>
          <Dialog.Content layerStyle="surfacePopover" maxW="460px" w="calc(100vw - 32px)">
            <Dialog.Header borderBottomWidth="1px" borderColor="ui.border.subtle">
              <Dialog.Title fontSize="md" fontWeight="800">
                Share thread
              </Dialog.Title>
              <Dialog.Description fontSize="sm" color="ui.text.secondary">
                Control read-only access for members of this workspace.
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Body py="4">
              <VStack align="stretch" gap="4">
                <HStack justify="space-between" gap="4">
                  <Box>
                    <Text fontSize="sm" fontWeight="700" color="ui.text.primary">
                      Workspace members can view
                    </Text>
                    <Text mt="1" fontSize="xs" color="ui.text.secondary">
                      Members can read the transcript and sources but cannot change this thread.
                    </Text>
                  </Box>
                  <Switch.Root
                    size="md"
                    checked={visibility === 'workspace'}
                    disabled={saving}
                    onCheckedChange={event => void handleToggle(event.checked)}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control />
                  </Switch.Root>
                </HStack>

                <Box
                  p="3"
                  borderWidth="1px"
                  borderColor="ui.border.subtle"
                  borderRadius="12px"
                  bg="ui.surface.input"
                >
                  <HStack align="start" gap="2">
                    <Link2 size={16} aria-hidden="true" />
                    <Text fontSize="xs" color="ui.text.secondary" lineHeight="1.5">
                      Members use Fork to continue to make an independent private copy. Revoking
                      access here does not revoke forks that already exist.
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer gap="2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Done
              </Button>
              <Button onClick={() => void handleCopyLink()} disabled={saving}>
                <Copy size={15} />
                Copy link
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
